import { buttonClassName, ButtonVariant } from '@/components/form/buttonHelper';
import { FileInput } from '@/components/form/fields/file/FileInput';
import { Step } from '@/components/multistep/Questionnaire';
import { useControls } from '@/components/multistep/useMultistep';
import SiDownload from '@/icons/SiDownload';
import { useCSV } from '@/services/csv/useCSVService';
import { useCallback, useMemo } from 'react';
import { ProductCSVParser } from '../bulkUploadHelper';
import { useBulkProductSmartNext } from './useBulkProductSmartNext';

const createTemplate = () => {
  const headings = [
    'Name',
    'ManufacturerSKU',
    'Manufacturer',
    'Category',
    'Description',
    'Price',
    'Region',
    'BasicBetterBest',
    'Margin',
    'Replacements',
    'Spec:CPU',
    'Spec:GPU'
  ];
  const examples = [
    [
      'Example Laptop',
      'Laptop1',
      'Dell',
      'Laptop',
      'A description of a laptop.',
      '713.27',
      'UK',
      'Basic',
      '10',
      'delllaptop2;delllaptop3',
      'Core 8 CPU',
      'Green GPU'
    ]
  ];

  const head = headings.join(',');
  return examples.reduce((template, row) => {
    return template + '\n' + row.join(',');
  }, head);
};

export const UploadStep = () => {
  const [onComplete, canMoveOn] = useCSV(state => [state.onComplete, !!state.result]);
  const { skipToStep } = useControls();
  const next = useBulkProductSmartNext();

  const template = useMemo(() => {
    const template = createTemplate();
    return URL.createObjectURL(new Blob([template], { type: 'octet/stream' }));
  }, []);

  const processAndProceed = async (file: File) => {
    // Create an instance of a worker using comlink syntactic sugar
    const instance = new ComlinkWorker<ProductCSVParser>(new URL('../CSVParsingWorker', import.meta.url));

    // This is specific to this process using a Product validation
    // We could pass a zod schema to the worker and have this be generic but the performance hit is too great
    const result = await instance.processProductCSV(file);

    onComplete(result);

    // If there any issues, show them
    if (
      result.errors.couldNotParse.length ||
      result.errors.couldNotValidate.length ||
      result.errors.genericErrors?.length
    ) {
      skipToStep('issues');
      return;
    }

    await next(result);
  };

  const validate = useCallback(() => canMoveOn, [canMoveOn]);
  return (
    <Step
      validate={validate}
      controls="manual"
      id="upload"
      title="Upload"
      description="Upload a spreadsheet of products to bulk upload them to the system."
    >
      <div className="flex w-full flex-col gap-2">
        <a className={buttonClassName(ButtonVariant.Mono)} href={template} download="bulk-upload-template.csv">
          <SiDownload />
          <span>Download template</span>
        </a>
        <FileInput
          id="csv"
          placeholder="Drop or click to upload spreadsheet"
          value={[]}
          onChange={([file]) => processAndProceed(file)}
        />
      </div>
    </Step>
  );
};
